import { useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  AreaChart,
  Area,
  XAxis,
} from "recharts";
import { CATEGORII } from "@/lib/categories";
import { formateazaRON } from "@/lib/settlement";

// Data (yyyy-mm-dd) din ISO, pentru input[type=date].
const ziISO = (iso) => new Date(iso).toISOString().slice(0, 10);

// Grafic Premium: plăcintă pe categorii + evoluție în timp cu interval reglabil.
export function SpendingChart({ expenses, members = [] }) {
  const toateZilele = expenses.map((e) => ziISO(e.createdAt)).sort();
  const [from, setFrom] = useState(toateZilele[0] || "");
  const [to, setTo] = useState(toateZilele[toateZilele.length - 1] || "");

  // Filtrăm cheltuielile în intervalul ales.
  const inInterval = expenses.filter((e) => {
    const zi = ziISO(e.createdAt);
    if (from && zi < from) return false;
    if (to && zi > to) return false;
    return true;
  });

  // Total pe categorie (plăcintă).
  const peCategorie = CATEGORII.map((c) => ({
    name: c.label,
    value: inInterval
      .filter((e) => (e.category || "altele") === c.id)
      .reduce((s, e) => s + e.amount, 0),
    hex: c.hex,
  })).filter((d) => d.value > 0);

  // Cheltuieli pe zile (evoluție în timp).
  const peZi = {};
  [...inInterval]
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    .forEach((e) => {
      const zi = new Date(e.createdAt).toLocaleDateString("ro-RO", {
        day: "numeric",
        month: "short",
      });
      peZi[zi] = (peZi[zi] || 0) + e.amount;
    });
  const inTimp = Object.entries(peZi).map(([zi, total]) => ({ zi, total }));

  // Cât a plătit fiecare membru (raport pe persoană) — în intervalul ales.
  const peMembru = members
    .map((m) => ({
      name: m.name,
      value: inInterval
        .filter((e) => e.paidBy === m.id)
        .reduce((s, e) => s + e.amount, 0),
    }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
  const maxMembru = peMembru.reduce((mx, d) => Math.max(mx, d.value), 0);

  const tooltipStyle = {
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    fontSize: 12,
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
      {/* Interval de date */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <label className="text-[10px] text-slate-400 font-semibold">De la</label>
          <input
            data-testid="chart-from-date"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-full h-9 rounded-xl border border-slate-200 px-2 text-sm outline-none focus:border-emerald-500"
          />
        </div>
        <div className="flex-1">
          <label className="text-[10px] text-slate-400 font-semibold">Până la</label>
          <input
            data-testid="chart-to-date"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full h-9 rounded-xl border border-slate-200 px-2 text-sm outline-none focus:border-emerald-500"
          />
        </div>
      </div>

      {peCategorie.length === 0 ? (
        <p className="text-center text-slate-400 text-sm py-6">
          Nicio cheltuială în intervalul ales.
        </p>
      ) : (
        <>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">Pe categorii</p>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={peCategorie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {peCategorie.map((d, i) => (
                    <Cell key={i} fill={d.hex} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => [formateazaRON(v), n]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Legenda */}
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-1">
              {peCategorie.map((d, i) => (
                <span key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: d.hex }}
                  />
                  {d.name} · {formateazaRON(d.value)}
                </span>
              ))}
            </div>
          </div>

          {peMembru.length > 0 && (
            <div data-testid="per-person-report">
              <p className="text-xs font-semibold text-slate-500 mb-2">
                Cât a cheltuit fiecare
              </p>
              <div className="space-y-2">
                {peMembru.map((d, i) => (
                  <div key={i} data-testid={`per-person-${i}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700">{d.name}</span>
                      <span className="font-bold text-slate-800">
                        {formateazaRON(d.value)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{
                          width: `${maxMembru ? (d.value / maxMembru) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {inTimp.length > 1 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">În timp</p>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={inTimp} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="chitArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="zi"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v) => [formateazaRON(v), "Cheltuit"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="url(#chitArea)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
